import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, ArrowLeft, ArrowRight, Tag } from 'lucide-react';
import { useDataService } from '../context/DataContext';
import useResponsive from '../hooks/useResponsive';
import LoadingSpinner from '../components/ui/LoadingSpinner';

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { isMobile } = useResponsive();
  const dataService = useDataService();

  useEffect(() => {
    const loadProjects = async () => {
      try {
        setLoading(true);
        const projectsData = await dataService.getProjects();
        // Handle both array and object with projects property
        const projectsList = Array.isArray(projectsData) 
          ? projectsData 
          : (projectsData.projects || []);
        // Sort by order field (ascending - lower order numbers first)
        const sortedProjects = [...projectsList].sort((a, b) => {
          const orderA = a.order ?? 999; // Default to high number if order is missing
          const orderB = b.order ?? 999;
          return orderA - orderB;
        });
        setProjects(sortedProjects);
      } catch (err) {
        console.error('Error loading projects:', err);
        setError(err.message);
        // Fallback to mock data
        try {
          const { projects: mockProjects } = await import('../data/mock');
          setProjects(mockProjects);
        } catch (mockErr) {
          console.error('Error loading mock projects:', mockErr);
        }
      } finally {
        setLoading(false);
      }
    };

    loadProjects();
  }, [dataService]);


  const getProjectUrl = (project) => {
    // Priority: demo > article > github
    if (project.links?.demo) return project.links.demo;
    if (project.links?.article) return project.links.article;
    if (project.links?.github) return project.links.github;
    return null;
  };

      if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  if (error && projects.length === 0) {
    return (
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
        <h2 className="text-xl font-semibold text-gray-900 mb-2">Unable to load initiatives</h2>
        <p className="text-gray-600 mb-4">{error}</p>
        <Link 
          to="/"
          className="text-gray-900 hover:text-gray-700 font-medium transition-colors"
        >
          Back
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Navigation */}
      <nav className="mb-8">
        <Link 
          to="/" 
          className="text-vintage-faded hover:text-vintage-accent flex items-center space-x-2 transition-colors"
        >
          <ArrowLeft size={16} />
            <span>Back</span>
        </Link>
      </nav>

      {/* Header */}
      <header className="mb-12 text-center">
        <h1 className="vintage-heading-1 text-4xl md:text-5xl text-vintage-ink mb-4">
          Initiatives
        </h1>
        <p className="vintage-body text-vintage-faded max-w-3xl mx-auto">
          Impactful initiatives spanning social initiatives and technical implementations
        </p>
      </header>

      {/* Initiatives List */}
      {projects.length > 0 ? (
        <div className="space-y-8">
          {projects.map((project) => {
            const projectUrl = getProjectUrl(project);
            return (
              <article key={project.id} className="vintage-card p-8 hover:shadow-lg transition-shadow">
                <div className="flex flex-col md:flex-row md:items-start md:justify-between mb-4">
                  <h3 className="vintage-heading-2 text-2xl text-vintage-ink mb-3 md:mb-0 flex-1">
                    {project.title}
                  </h3>
                  {project.period && (
                    <div className="flex items-center space-x-1 text-sm text-vintage-faded md:ml-4">
                      <Calendar size={14} />
                      <span>{project.period}</span>
                    </div>
                  )}
                </div>
                
                <p className="vintage-body text-vintage-faded leading-relaxed mb-4">
                  {project.description}
                </p>
                
                {project.tags && project.tags.length > 0 && (
                  <div className="flex items-center flex-wrap gap-2 mb-4">
                    {project.tags.slice(0, 5).map((tag) => (
                      <span key={tag} className="vintage-caption text-vintage-faded flex items-center">
                        <Tag size={12} className="mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                
                <div className="flex items-center justify-between pt-4 border-t" style={{ borderTopColor: 'var(--vintage-accent)' }}>
                  <div className="flex items-center space-x-2">
                    {project.featured && (
                      <span className="vintage-badge bg-vintage-accent text-vintage-cream">
                        Featured
                      </span>
                    )}
                    {project.category && (
                      <span className="vintage-badge">
                        {project.category}
                      </span>
                    )}
                  </div>
                  {projectUrl && (
                    <a 
                      href={projectUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-vintage-accent hover:text-vintage-ink font-medium transition-colors flex items-center space-x-1"
                    >
                      <span>View</span>
                      <ArrowRight size={14} />
                    </a>
                  )}
                </div>
              </article>
            );
          })}
        </div>
      ) : (
        <div className="text-center py-12">
          <p className="vintage-body text-vintage-faded mb-4">No initiatives found.</p>
          <Link 
            to="/"
            className="text-vintage-accent hover:text-vintage-ink font-medium transition-colors"
          >
            Back
          </Link>
        </div>
      )}
    </div>
  );
};

export default Projects;

